import { useCallback, useEffect, useState } from "react";
import WelcomePopupPreview from "../../components/admin/WelcomePopupPreview";
import Spinner from "../../components/ui/Spinner";
import { Help, HowItWorks, PersonCell, SectionCard, StatusPill, btnPrimary, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { promotionsService } from "../../features/promotions/service";
import type { PersonRef, WelcomeBonusConfig } from "../../features/promotions/types";
import { MAX_CREDITS, parseIntStrict } from "./promotionsPageUtils";

// ── Welcome Bonus panel ──────────────────────────────────────────────────────
export default function WelcomeBonusPanel({ onToast, onSummary }: { onToast: ReturnType<typeof useAdminToast>; onSummary: (s: string) => void }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<PersonRef | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  // Empty until the saved config arrives: no amount is assumed client-side.
  const [seller, setSeller] = useState("");
  const [buyer, setBuyer] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const c = await promotionsService.getWelcomeBonus();
      setEnabled(c.enabled);
      setSeller(String(c.seller_credits));
      setBuyer(String(c.buyer_credits));
      setTitle(c.title ?? "");
      setMessage(c.message ?? "");
      setUpdatedAt(c.updated_at ?? null);
      setUpdatedBy(c.updated_by_user ?? null);
      setLoadError(null);
      setLoaded(true);
      onSummary(c.enabled ? "Welcome bonus on" : "Welcome bonus off");
    } catch (e) {
      setLoaded(false);
      setLoadError(errMsg(e, "Failed to load welcome bonus"));
    } finally {
      setLoading(false);
    }
  }, [onSummary]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!loaded) return;
    const s = parseIntStrict(seller);
    const b = parseIntStrict(buyer);
    if (s === null || s > MAX_CREDITS) return onToast.showError(`Seller credits must be a whole number 0-${MAX_CREDITS}`);
    if (b === null || b > MAX_CREDITS) return onToast.showError(`Buyer credits must be a whole number 0-${MAX_CREDITS}`);
    const body: WelcomeBonusConfig = {
      enabled,
      seller_credits: s,
      buyer_credits: b,
      title: title.trim() || null,
      message: message.trim() || null,
    };
    setSaving(true);
    try {
      await promotionsService.updateWelcomeBonus(body);
      onToast.showSuccess("Welcome bonus saved");
      await load();
    } catch (e) {
      onToast.showError(errMsg(e, "Failed to save welcome bonus"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      id="welcome-bonus"
      title="Welcome bonus"
      summary={loaded && <StatusPill tone={enabled ? "green" : "gray"}>{enabled ? "Enabled" : "Disabled"}</StatusPill>}
      description="Free credits granted once when a user completes onboarding. Festive campaigns can override these amounts. Welcome credits never expire."
    >
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !loaded ? (
        <div className="flex flex-col items-start gap-3 py-2" role="alert">
          <p className="text-sm text-red-400">Couldn't load the current welcome bonus settings{loadError ? ` (${loadError})` : ""}, so editing is disabled to avoid overwriting them.</p>
          <button onClick={() => { setLoading(true); void load(); }} className={btnPrimary}>Retry</button>
        </div>
      ) : (
        <div className="space-y-4">
          <HowItWorks>
            <p>Every new user gets these free credits once, when they finish onboarding. A live festive campaign for their role can replace this amount.</p>
            <p>Changes apply immediately to the next user who finishes onboarding. Users who already onboarded are not affected.</p>
          </HowItWorks>
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Welcome bonus enabled
            </label>
            <Help>Untick to stop giving the base welcome bonus. Live campaigns can still grant their own amount.</Help>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Seller credits</label>
              <input type="number" min={0} max={MAX_CREDITS} className={inputCls} value={seller} onChange={(e) => setSeller(e.target.value)} placeholder="Credits" />
              <Help>Free credits a new seller gets on finishing onboarding. 0 = none.</Help>
            </div>
            <div>
              <label className={labelCls}>Buyer credits</label>
              <input type="number" min={0} max={MAX_CREDITS} className={inputCls} value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Credits" />
              <Help>Free credits a new buyer gets on finishing onboarding. 0 = none.</Help>
            </div>
          </div>
          <div>
            <label className={labelCls}>Popup title (optional — blank uses default copy)</label>
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Welcome to Gullivr!" />
            <Help>Heading of the popup the user sees after getting the bonus. Blank = default text.</Help>
          </div>
          <div>
            <label className={labelCls}>Popup message (optional — use {"{credits}"} for the amount)</label>
            <textarea className={inputCls} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} placeholder="You've got {credits} free credits — on us." />
            <Help>Popup body text. {"{credits}"} is replaced by the amount the user received. Blank = default text.</Help>
          </div>
          <WelcomePopupPreview sellerCredits={seller} buyerCredits={buyer} title={title} message={message} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button onClick={save} disabled={saving} className={btnPrimary}>{saving ? "Saving…" : "Save welcome bonus"}</button>
            {updatedAt && (
              <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-1">
                Last changed {formatISTDateTime(updatedAt)}{updatedBy && <>by <PersonCell person={updatedBy} fallback="an admin" /></>}
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}
